/**
 * AddEmployeeDialog — the "+" on My Team.
 * @file src/web/features/team/components/AddEmployeeDialog.tsx
 *
 * @llm-rule WHEN: A supervisor adds someone to their team
 * @llm-rule NOTE: The rank list offered is strictly below the viewer's own.
 *   The server enforces the same rule; this just avoids offering a doomed
 *   choice. The generated password is shown once and stored nowhere readable.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@bloomneo/uikit';
import { Copy } from 'lucide-react';
import { useAppRequest } from '../../../shared/hooks/useAppApi';
import {
  canAssignHeadDesignation,
  COMPANY_ROLES,
  HEAD_DESIGNATIONS,
  rankOf,
  ROLE_LABEL,
  type HeadDesignation,
  type OrgRole,
} from '../../../shared/domain';

interface Team {
  id: string;
  name: string;
  department: { id: string; name: string };
}

export function AddEmployeeDialog({
  open,
  onOpenChange,
  viewerRole,
  viewerDepartmentId,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  viewerRole: OrgRole;
  viewerDepartmentId?: string | null;
  onAdded: () => void;
}) {
  const { request } = useAppRequest();

  const [teams, setTeams] = useState<Team[]>([]);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<{ email: string; password: string } | null>(null);

  const [form, setForm] = useState({
    name: '',
    email: '',
    title: '',
    orgRole: 'EMPLOYEE' as OrgRole,
    /** '' means no designation. Only ever sent for a HEAD. */
    headDesignation: '' as HeadDesignation | '',
    teamId: '',
  });

  useEffect(() => {
    if (!open) return;
    void request<{ teams: Team[] }>('/api/org/teams').then((r) => {
      if (r.ok) setTeams(r.data.teams);
    });
  }, [open, request]);

  /*
    Who this viewer may create.

    Your OWN rank is included, because a company has as many Heads as it has
    departments and they are added by the people already doing the job — the
    old "strictly below you" rule meant a Head could never add the Head of
    another department, so every one of them had to go back to the Company
    Admin. Whoever is created gets a login here exactly as the first admin did
    when the company was set up.

    COMPANY_ADMIN is the one rank still not creatable from this screen: the
    company has one, it is set up with the company, and handing out a second
    from here would be a silent transfer of ownership.
  */
  const creatableRoles = useMemo(
    () =>
      COMPANY_ROLES.filter((r) => r !== 'COMPANY_ADMIN' && rankOf(r) <= rankOf(viewerRole)),
    [viewerRole],
  );

  async function copy(text: string, label: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.info(`Copy manually: ${text}`);
    }
  }

  async function submit() {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required');
      return;
    }

    setBusy(true);
    try {
      const result = await request<{
        person: { name: string | null; email: string };
        temporaryPassword?: string;
      }>('/api/org/people', {
        method: 'POST',
        body: {
          name: form.name,
          email: form.email,
          title: form.title || undefined,
          orgRole: form.orgRole,
          // Only a HEAD carries one, and only a Company Admin may set it.
          ...(form.orgRole === 'HEAD' && form.headDesignation
            ? { headDesignation: form.headDesignation }
            : {}),
          teamId: form.teamId || undefined,
          departmentId:
            teams.find((t) => t.id === form.teamId)?.department.id ?? viewerDepartmentId ?? undefined,
        },
      });

      if (!result.ok) {
        toast.error(result.error);
        return;
      }

      toast.success(`${result.data.person.name} added`);
      if (result.data.temporaryPassword) {
        setIssued({
          email: result.data.person.email,
          password: result.data.temporaryPassword,
        });
      }
      setForm({ name: '', email: '', title: '', orgRole: 'EMPLOYEE', headDesignation: '', teamId: '' });
      onAdded();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setIssued(null);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add an employee</DialogTitle>
          <DialogDescription>
            They’ll enrol their face on first sign-in, and you’ll get their sign-in details once
            to hand over. You can create your own rank and anything under it.
          </DialogDescription>
        </DialogHeader>

        {issued ? (
          // One-time credentials. Nothing readable is kept, so this is the
          // only moment the password exists in plain text.
          <div className="rounded-xl border border-[var(--state-warn-border)] bg-[var(--state-warn-bg)] p-4">
            <div className="text-[13px] font-semibold text-ink">
              Sign-in details — shown once
            </div>

            <div className="mt-2.5 flex flex-col gap-1.5">
              {[
                ['Username', issued.email],
                ['Password', issued.password],
              ].map(([label, value]) => (
                <div key={label} className="flex items-center gap-2">
                  <span className="w-[74px] text-[11px] uppercase tracking-[0.1em] text-ink-ghost">
                    {label}
                  </span>
                  <code className="flex-1 rounded bg-paper px-2 py-1 font-mono text-[13px] text-ink">
                    {value}
                  </code>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2"
                    onClick={() => void copy(value, label)}
                  >
                    <Copy className="size-3" />
                  </Button>
                </div>
              ))}
            </div>

            <p className="mt-2 text-[11.5px] leading-relaxed text-ink-soft">
              Hand this over. If it is lost, issue a new one from the People list — it cannot be
              looked up.
            </p>

            <div className="mt-3 flex gap-2">
              <Button size="sm" onClick={() => setIssued(null)}>
                Add another
              </Button>
              <Button size="sm" variant="outline" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Name" required>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Arun Kumar"
                />
              </FormField>

              <FormField label="Email" required>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="arun@company.com"
                />
              </FormField>

              <FormField label="Rank" required>
                <Select
                  value={form.orgRole}
                  onValueChange={(v) =>
                    setForm({
                      ...form,
                      orgRole: v as OrgRole,
                      // A designation only exists on a Head — drop it otherwise.
                      headDesignation: v === 'HEAD' ? form.headDesignation : '',
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {creatableRoles.map((r) => (
                      <SelectItem key={r} value={r}>
                        {ROLE_LABEL[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              {form.orgRole === 'HEAD' && canAssignHeadDesignation(viewerRole) && (
                <FormField
                  label="Designation"
                  helper="Optional. A label only — it grants no extra powers."
                >
                  <Select
                    value={form.headDesignation || 'NONE'}
                    onValueChange={(v) =>
                      setForm({
                        ...form,
                        headDesignation: v === 'NONE' ? '' : (v as HeadDesignation),
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NONE">No designation</SelectItem>
                      {HEAD_DESIGNATIONS.map((d) => (
                        <SelectItem key={d} value={d}>
                          {d}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
              )}

              <FormField label="Title" helper="Shown beside their name">
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Senior Engineer"
                />
              </FormField>

              {teams.length > 0 && (
                <FormField label="Team" className="sm:col-span-2">
                  <Select
                    value={form.teamId}
                    onValueChange={(v) => setForm({ ...form, teamId: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Pick a team" />
                    </SelectTrigger>
                    <SelectContent>
                      {teams.map((team) => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.name} · {team.department.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                Cancel
              </Button>
              <Button
                onClick={() => void submit()}
                disabled={busy || !form.name.trim() || !form.email.trim()}
              >
                {busy ? 'Adding…' : 'Add employee'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
